/**
 * One-shot, in-memory handoff of a file dropped on a use-case page to the
 * converter.
 *
 * A `File` cannot travel through a URL, and persisting it (sessionStorage,
 * IndexedDB) would outlive the navigation it belongs to. The use-case page
 * stores the file here and navigates to `/:lang?preset=<slug>`; ConvertPage
 * takes it exactly once on mount. A full page reload simply loses it, which is
 * the right outcome: the visitor lands on the converter with the preset
 * applied and picks the file again.
 */

let pending: File | null = null;

export function setPendingConversion(file: File): void {
  pending = file;
}

/** Returns the pending file (if any) and clears it. */
export function takePendingConversion(): File | null {
  const file = pending;
  pending = null;
  return file;
}

/**
 * Hands the pending file (if any) to `onFile` on a microtask and clears it.
 * Delivering asynchronously lets a mount effect accept the file like any other
 * external event, instead of setting state synchronously inside the effect.
 */
export function deliverPendingConversion(onFile: (file: File) => void): void {
  const file = takePendingConversion();
  if (file) queueMicrotask(() => onFile(file));
}
